// react
import React from 'react';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

// Optional: improve touch interactions
// if (typeof Highcharts === 'object' && Highcharts?.Pointer) {
//   Highcharts.Pointer.prototype.reset = function () {
//     return undefined;
//   };
// }

/**
 * DistributionAlarmChart
 * Stacked area (percent) chart showing category distribution over time.
 * For now it uses dummy data and EXCLUDES the keys: "IkkeAnalyseret" and "Analyseret".
 */
export default function DistributionChart({ data = [] }) {
  // Normalize timestamps like "2025-09-08T10:00:00+0000" -> "2025-09-08T10:00:00+00:00"
  const toEpoch = (ts) => {
    if (!ts) return NaN;
    // If it ends with +HHMM or -HHMM, insert a colon for ISO compliance
    const normalized = ts.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
    const d = new Date(normalized);
    return d.getTime();
  };

  // Figure out which keys to include from the first row
  const includeKeys = React.useMemo(() => {
    const EXCLUDE_KEYS = new Set(['Analyseret', 'timestamp']);
    if (!data || data.length === 0) return [];
    return Object.keys(data[0]).filter((k) => !EXCLUDE_KEYS.has(k));
  }, [data]);

  // Convert to Highcharts series
  const series = React.useMemo(() => {
    return includeKeys.map((key) => ({
      type: 'area',
      name: key,
      data: (data || []).map((p) => [
        toEpoch(p.timestamp),
        Number(p?.[key] ?? 0),
      ]),
      marker: { enabled: false },
    }));
  }, [data, includeKeys]);

  const options = {
    chart: {
      type: 'area',
      height: 360,
      spacing: [16, 16, 8, 8],
      className: 'rounded-2xl shadow-sm',
    },
    title: { text: '' },
    credits: { enabled: false },
    legend: {
      align: 'center',
      verticalAlign: 'bottom',
      layout: 'horizontal',
    },
    time: { useUTC: true },
    xAxis: {
      type: 'datetime',
      tickLength: 0,
      crosshair: true,
      title: { text: undefined },
    },
    yAxis: {
      min: 0,
      max: 100,
      title: { text: 'Andel (%)' },
      labels: {
        formatter: function () {
          return this.value + '%';
        },
      },
    },
    tooltip: {
      shared: true,
      xDateFormat: '%e %b %Y %H:%M',
      pointFormatter: function () {
        const y = this.y;
        const pct = this.percentage;

        // small colored dot styled like Highcharts does by default
        const colorDot = `<span style="color:${this.series.color}; font-size:1em;">●</span>`;

        return `${colorDot} <span style="font-weight:600;">${this.series.name}</span>: 
      <b>${Highcharts.numberFormat(y, 2)}</b> 
      (<b>${Highcharts.numberFormat(pct, 0)}%</b>)<br/>`;
      },
    },
    plotOptions: {
      series: {
        stacking: 'percent',
        animation: { duration: 300 },
        states: { hover: { enabled: true } },
      },
      area: {
        lineWidth: 1,
        fillOpacity: 0.6,
      },
    },
    series,
    accessibility: { enabled: false },
  };

  return (
    <div className="w-full">
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  );
}
